/**
 * Tiny inline markdown renderer for help content.
 *
 * Supports:
 *  - paragraphs (blank-line separated)
 *  - `**bold**`
 *  - `*italic*`
 *  - `` `inline code` ``
 *  - `[text](url)` links
 *  - `- item` unordered lists (consecutive lines)
 *  - `1. item` ordered lists (consecutive lines)
 *
 * No external deps. Returns plain React nodes.
 */
import type { ReactNode } from 'react';

type Block =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] };

const ORDERED_ITEM = /^\d+\.\s+(.*)$/;

function blockify(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let buffer: string[] = [];
  let listBuffer: string[] = [];
  let listOrdered = false;

  function flushParagraph() {
    if (buffer.length === 0) return;
    blocks.push({ kind: 'paragraph', text: buffer.join(' ').trim() });
    buffer = [];
  }
  function flushList() {
    if (listBuffer.length === 0) return;
    blocks.push({ kind: 'list', ordered: listOrdered, items: listBuffer.slice() });
    listBuffer = [];
  }
  // Start (or continue) a list of the given kind, flushing a list of the other
  // kind first so ordered and unordered runs don't merge.
  function pushListItem(item: string, ordered: boolean) {
    flushParagraph();
    if (listBuffer.length > 0 && listOrdered !== ordered) flushList();
    listOrdered = ordered;
    listBuffer.push(item);
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    const ordered = ORDERED_ITEM.exec(line);
    if (line.startsWith('- ')) {
      pushListItem(line.slice(2).trim(), false);
      continue;
    }
    if (ordered) {
      pushListItem((ordered[1] ?? '').trim(), true);
      continue;
    }
    flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Render a single line of inline markdown. */
function renderInline(line: string, keyPrefix: string): ReactNode[] {
  // Walk the string char by char, picking up runs of bold/italic/code/text.
  const out: ReactNode[] = [];
  let i = 0;
  let textBuf = '';
  let counter = 0;
  function pushText() {
    if (textBuf.length === 0) return;
    out.push(textBuf);
    textBuf = '';
  }
  while (i < line.length) {
    const ch = line[i];
    if (ch === '[') {
      // link: [text](href)
      const closeText = line.indexOf('](', i + 1);
      const closeHref = closeText === -1 ? -1 : line.indexOf(')', closeText + 2);
      if (closeText !== -1 && closeHref !== -1) {
        pushText();
        const text = line.slice(i + 1, closeText);
        const href = line.slice(closeText + 2, closeHref);
        out.push(
          <a
            key={`${keyPrefix}-a-${counter++}`}
            href={href}
            className="font-medium text-[color:var(--color-primary)] underline hover:no-underline"
          >
            {renderInline(text, `${keyPrefix}-a${counter}`)}
          </a>,
        );
        i = closeHref + 1;
        continue;
      }
      textBuf += ch;
      i += 1;
      continue;
    }
    if (ch === '`') {
      // inline code: scan until next backtick
      const end = line.indexOf('`', i + 1);
      if (end === -1) {
        textBuf += ch;
        i += 1;
        continue;
      }
      pushText();
      out.push(
        <code
          key={`${keyPrefix}-c-${counter++}`}
          className="rounded bg-[color:var(--color-muted)] px-1 py-0.5 font-mono text-[11px]"
        >
          {line.slice(i + 1, end)}
        </code>,
      );
      i = end + 1;
      continue;
    }
    if (ch === '*' && line[i + 1] === '*') {
      // bold: find matching '**'
      const end = line.indexOf('**', i + 2);
      if (end === -1) {
        textBuf += ch;
        i += 1;
        continue;
      }
      pushText();
      out.push(
        <strong key={`${keyPrefix}-b-${counter++}`} className="font-semibold">
          {renderInline(line.slice(i + 2, end), `${keyPrefix}-b${counter}`)}
        </strong>,
      );
      i = end + 2;
      continue;
    }
    if (ch === '*') {
      // italic: find next single '*'
      const end = line.indexOf('*', i + 1);
      if (end === -1) {
        textBuf += ch;
        i += 1;
        continue;
      }
      pushText();
      out.push(
        <em key={`${keyPrefix}-i-${counter++}`} className="italic">
          {renderInline(line.slice(i + 1, end), `${keyPrefix}-i${counter}`)}
        </em>,
      );
      i = end + 1;
      continue;
    }
    textBuf += ch;
    i += 1;
  }
  pushText();
  return out;
}

/** Render the given markdown source as React nodes. */
export function renderMarkdown(src: string): ReactNode {
  const blocks = blockify(src);
  return blocks.map((block, idx) => {
    if (block.kind === 'paragraph') {
      return (
        <p key={`md-p-${idx}`} className="text-xs leading-relaxed">
          {renderInline(block.text, `md-p-${idx}`)}
        </p>
      );
    }
    const ListTag = block.ordered ? 'ol' : 'ul';
    return (
      <ListTag
        key={`md-l-${idx}`}
        className={`${block.ordered ? 'list-decimal' : 'list-disc'} space-y-0.5 pl-5 text-xs leading-relaxed`}
      >
        {block.items.map((item, j) => (
          <li key={`md-l-${idx}-${j}`}>{renderInline(item, `md-l-${idx}-${j}`)}</li>
        ))}
      </ListTag>
    );
  });
}
